import React, { ReactNode } from 'react';
import { useIntl } from '@leagueplatform/locales';
import { useParamsLegacy as useParams } from '@leagueplatform/routing';
import { Box, genesisStyled, css, Link } from '@leagueplatform/genesis-commons';
import {
  ChallengeIcon,
  ICON_TYPES,
} from '#components/challenge-icon/challenge-icon.component.tsx';
import { track } from '@leagueplatform/analytics';
import { CHALLENGE_CAMPAIGN_TYPE } from '#constants/analytics.constants.tsx';
import { useGetChallengeByIdData } from '#pages/challenge-landing-page/hooks/use-get-challenge-by-id-data.hook.ts';
import {
  SettingsDropdown,
  SETTINGS_BUTTON_LABEL,
} from '../settings-dropdown/settings-dropdown.component';

const ACTIVE_BUTTON_STYLES = {
  backgroundColor: 'primary.background.pressed',
  color: 'onSurface.text.reversed',
  textDecoration: 'none',
};
const FOCUS_BUTTON_STYLES = {
  backgroundColor: 'primary.background.hovered',
  color: 'onSurface.text.reversed',
  textDecoration: 'none',
};

const SettingsButton = genesisStyled(Link)(() =>
  css({
    '&:hover': FOCUS_BUTTON_STYLES,
    '&:focus': FOCUS_BUTTON_STYLES,
    '&:active': ACTIVE_BUTTON_STYLES,
  }),
);

export interface SettingsCogProps {
  children: ReactNode;
  modalIsOpen: boolean;
  isSettingsDropdownOpen: boolean;
  setIsSettingsDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const SettingsCog = ({
  children,
  modalIsOpen,
  isSettingsDropdownOpen,
  setIsSettingsDropdownOpen,
}: SettingsCogProps) => {
  const { formatMessage } = useIntl();
  const { challengeId } = useParams<{ challengeId: string }>();
  const { data: challengeData } = useGetChallengeByIdData();

  return (
    <Box position="relative">
      <SettingsButton
        as="button"
        id={SETTINGS_BUTTON_LABEL}
        alignItems="center"
        aria-expanded={isSettingsDropdownOpen}
        aria-haspopup="true"
        aria-label={formatMessage({ id: 'SETTINGS' })}
        border="none"
        backgroundColor="surface.background.primary"
        display="flex"
        onClick={() => {
          setIsSettingsDropdownOpen(!isSettingsDropdownOpen);
          if (!isSettingsDropdownOpen) {
            track(
              'challenges.button_clicked.health_campaign_detail.navigate_to_challenge_settings',
              {
                health_campaign_id: challengeId ?? '',
                health_campaign_name: challengeData?.data.title ?? '',
                health_campaign_type: CHALLENGE_CAMPAIGN_TYPE,
                challenge_status: challengeData?.data.status ?? '',
              },
            );
          }
        }}
        paddingX="one"
        paddingY="threeQuarters"
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(isSettingsDropdownOpen && ACTIVE_BUTTON_STYLES)}
      >
        <ChallengeIcon iconType={ICON_TYPES.COG} />
        {/*
          We want to display the word "Settings" when tablet and above,
          but visually hide it on smaller screen sizes. Button has an
          aria-label for consistent labeling
        */}
        <Box
          as="span"
          display={{ _: 'none', tablet: 'block' }}
          marginInlineStart="half"
        >
          {formatMessage({ id: 'SETTINGS' })}
        </Box>
      </SettingsButton>
      {isSettingsDropdownOpen && (
        <SettingsDropdown
          isOpen={isSettingsDropdownOpen}
          setIsOpen={setIsSettingsDropdownOpen}
          modalIsOpen={modalIsOpen}
        >
          {children}
        </SettingsDropdown>
      )}
    </Box>
  );
};
